import {
    defineComponent,
} from "vue";
import { getPublicServerCanonicalReference } from "@logscord/public-server";
import {
    PublicFooter,
    PublicNavbar,
    usePublicLocalizedPath,
} from "./PublicShell.js";
import { useLocale } from "../../locales/i18n.js";
import {
    PublicServerAvatar,
    PublicServerJoinModal,
    formatDirectoryCount,
    isServerJoinDisabled,
    openOrExplainServerJoin,
} from "./serverDirectoryShared.js";
import { usePublicServerDirectory } from "./usePublicServerDirectory.js";
import PublicServerDirectoryFilters, {
    PublicServerDirectorySearch,
} from "./PublicServerDirectoryFilters.js";

export default defineComponent({
    setup() {
        const { i18n, locale } = useLocale();
        const publicPath = usePublicLocalizedPath();
        const {
            data,
            loading,
            failed,
            joinModalServer,
            search,
            membersMin,
            membersMax,
            onlineMin,
            onlineMax,
            voiceMin,
            voiceMax,
            sort,
            applyFilters,
            resetFilters,
            goToPage,
        } = usePublicServerDirectory();

        return () => (
            <div class="public-page server-directory-page">
                <PublicNavbar />

                <main class="server-directory-main">
                    <header class="server-directory-intro">
                        <div>
                            <span class="landing-kicker">{i18n("servers.hero.kicker")}</span>
                            <h1>{i18n("servers.hero.title")}</h1>
                        </div>
                        <p>{i18n("servers.hero.description")}</p>
                    </header>

                    <PublicServerDirectorySearch
                        search={search}
                        translate={i18n}
                        apply={applyFilters}
                    />

                    <div class="server-directory-layout">
                        <section class="server-directory-results">
                            <div class="server-directory-results-header">
                                <div>
                                    <strong>{formatDirectoryCount(data.value.pagination.totalItems, locale.value)}</strong>
                                    <span>{i18n("servers.results.count")}</span>
                                </div>
                                <span>{i18n("servers.results.page")} {data.value.pagination.page}</span>
                            </div>

                            {failed.value && <div class="server-directory-state">{i18n("servers.error")}</div>}

                            {!failed.value && !loading.value && data.value.items.length === 0 && (
                                <div class="server-directory-state">{i18n("servers.empty")}</div>
                            )}

                            <div class="server-directory-grid">
                                {loading.value && Array.from({ length: 9 }).map((_, index) => (
                                    <div class="server-directory-card server-directory-skeleton" key={`skeleton-${index}`} />
                                ))}
                                {!loading.value && data.value.items.map((server) => (
                                    <article class="server-directory-card" key={server.guildId}>
                                        <router-link
                                            class="server-directory-card-main"
                                            to={publicPath(`/servers/${getPublicServerCanonicalReference(server)}`)}
                                        >
                                            <PublicServerAvatar server={server} />
                                            <div>
                                                <h2>{server.name}</h2>
                                                <p>{server.description || i18n("servers.card.no_description")}</p>
                                            </div>
                                        </router-link>

                                        <div class="server-directory-card-metrics">
                                            <span><i class="material-symbols-outlined">groups</i>{formatDirectoryCount(server.memberCount, locale.value)}</span>
                                            <span><i class="material-symbols-outlined">visibility</i>{formatDirectoryCount(server.approximateOnlineCount, locale.value)}</span>
                                            <span><i class="material-symbols-outlined">settings_voice</i>{formatDirectoryCount(server.currentVoiceCount, locale.value)}</span>
                                        </div>

                                        <button
                                            type="button"
                                            class={{
                                                "server-directory-join-button": true,
                                                "is-disabled": isServerJoinDisabled(server),
                                            }}
                                            onClick={() => openOrExplainServerJoin(server, (selected) => {
                                                joinModalServer.value = selected;
                                            })}
                                        >
                                            <i class="material-symbols-outlined">login</i>
                                            {i18n("servers.join.action")}
                                        </button>
                                    </article>
                                ))}
                            </div>

                            {data.value.pagination.totalPages > 1 && (
                                <nav class="server-directory-pagination" aria-label={i18n("servers.pagination.label")}>
                                    <button
                                        type="button"
                                        disabled={data.value.pagination.page <= 1}
                                        onClick={() => goToPage(data.value.pagination.page - 1)}
                                    >
                                        <i class="material-symbols-outlined">chevron_left</i>
                                        {i18n("servers.pagination.previous")}
                                    </button>
                                    <span>
                                        {data.value.pagination.page} / {data.value.pagination.totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={data.value.pagination.page >= data.value.pagination.totalPages}
                                        onClick={() => goToPage(data.value.pagination.page + 1)}
                                    >
                                        {i18n("servers.pagination.next")}
                                        <i class="material-symbols-outlined">chevron_right</i>
                                    </button>
                                </nav>
                            )}
                        </section>

                        <aside class="server-directory-filter-sidebar">
                            <div class="server-directory-filter-heading">
                                <i class="material-symbols-outlined">tune</i>
                                <div>
                                    <strong>{i18n("servers.filters.title")}</strong>
                                    <span>{i18n("servers.filters.description")}</span>
                                </div>
                            </div>

                            <PublicServerDirectoryFilters
                                filters={{
                                    search,
                                    membersMin,
                                    membersMax,
                                    onlineMin,
                                    onlineMax,
                                    voiceMin,
                                    voiceMax,
                                    sort,
                                }}
                                membersMaximum={data.value.bounds.membersMax}
                                translate={i18n}
                                apply={applyFilters}
                                reset={resetFilters}
                            />
                        </aside>
                    </div>
                </main>

                <PublicFooter />
                <PublicServerJoinModal
                    server={joinModalServer.value}
                    translate={i18n}
                    locale={locale.value}
                    onClose={() => {
                        joinModalServer.value = null;
                    }}
                />
            </div>
        );
    },
});
